import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Archive, Eye, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { apiAffaires, apiClients, apiPersons, apiAffaireTeam, apiBudget, apiSettings } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import AffaireTasksTab from '../components/AffaireTasksTab.jsx';
import DatePicker from '../components/DatePicker.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';
import InternalAffaireBadge, { INTERNAL_AFFAIRE_TITLE } from '../components/InternalAffaireBadge.jsx';
import TabbedEditor from '../components/TabbedEditor.jsx';
import GescomProjectField from '../components/GescomProjectField.jsx';
import AffaireTeamTab from '../components/AffaireTeamTab.jsx';
import FilterBar from '../components/FilterBar.jsx';
import ArchiveSuggestionsModal from '../components/ArchiveSuggestionsModal.jsx';
import { SortableTh, sortRows, BulkActionsBar } from '@synotis/ui';
import { AFFAIRES_SORT, useSortStore } from '../hooks/useSortStore.js';
import { useFilterStore, toFilterQuery } from '../hooks/useFilterStore.js';
import { hourlyToTjm, tjmToHourly } from '../lib/units.js';
import { affaireLevel } from '../lib/affaireAccess.js';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const viewBtn = {
  background: 'transparent',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--text)',
};

const sectionTitle = {
  margin: '4px 0 6px',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const checkboxRow = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: 'var(--text)',
};

const BUDGET_TYPES = [
  { value: 'NONE', label: 'Aucun' },
  { value: 'HOURLY_FEE', label: 'Horaire' },
  { value: 'FEE_PER_PROJECT', label: 'Forfait projet' },
  { value: 'FEE_PER_PHASE', label: 'Forfait par phase' },
];

const PROJECT_STATUSES = [
  { value: 'TENTATIVE', label: 'Tentative' },
  { value: 'CONFIRMED', label: 'Confirmé' },
  { value: 'COMPLETED', label: 'Terminé' },
];

const STATUS_BG = {
  TENTATIVE: 'var(--warning)',
  CONFIRMED: 'var(--success)',
  COMPLETED: 'var(--text-muted)',
};

const EMPTY = {
  name: '',
  code: '',
  clientId: '',
  color: '326496',
  notes: '',
  budgetType: 'NONE',
  budgetTotal: '',
  budgetPerPhase: false,
  budgetPriority: false,
  defaultHourlyRate: '',
  nonBillable: false,
  tentative: false,
  status: 'TENTATIVE',
  lockedTaskList: false,
  allPmsSchedule: false,
  budgetAlertEnabled: false,
  planningOnly: false,
  // Ticket #54 — affaire interne (hors export Gescom) + expiration optionnelle.
  isInternal: false,
  expiresAt: '',
  budgetAlertThresholdPct: '',
  budgetAlertRemainingDays: '',
  startDate: '',
  endDate: '',
  projectManagerId: '',
  gescomProposalId: '',
  gescomOrderId: '',
  gescomProjectId: '',
};

/**
 * #108 — champs modifiables au niveau DÉLÉGUÉ (responsable client, PM, manager
 * hiérarchique du PM). Miroir de `affaireDelegatedUpdateSchema` côté API, qui
 * est `.strict()` : tout autre champ ferait échouer l'enregistrement en 400.
 */
const DELEGATED_FIELDS = [
  'name',
  'color',
  'notes',
  'budgetType',
  'budgetTotal',
  'budgetPerPhase',
  'budgetPriority',
  'defaultHourlyRate',
  'nonBillable',
  'tentative',
  'status',
  'lockedTaskList',
  'allPmsSchedule',
  'budgetAlertEnabled',
  'budgetAlertThresholdPct',
  'budgetAlertRemainingDays',
  'startDate',
  'endDate',
  'projectManagerId',
];

/** Badge « Planification » : affaire planifiable mais fermée aux imputations
 *  (proposition Synosight non encore gagnée). */
const PLANNING_ONLY_TITLE =
  "Planification uniquement : imputations de temps fermées tant que la proposition n'est pas gagnée";

function PlanningOnlyBadge() {
  return (
    <span
      title={PLANNING_ONLY_TITLE}
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 11,
        fontWeight: 500,
        background: 'var(--glass)',
        color: 'var(--warning)',
        border: '1px solid var(--warning)',
        whiteSpace: 'nowrap',
      }}
    >
      Planification
    </span>
  );
}

/** Sprint 23 — couleur du badge budget selon le statut. */
const BUDGET_STATUS_COLOR = {
  ok: 'var(--success)',
  warning: 'var(--warning)',
  over: 'var(--danger)',
};

function fmtChf(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 }).format(Math.round(n));
}

function fmtDays(n) {
  if (n == null) return '—';
  return `${new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 1 }).format(n)} j`;
}

/**
 * Sprint Budget journalier — détail métier (jours + TJM/CJM moyens observés).
 * 1 jour = 8h. Affiché sous le badge dans la modale d'édition.
 */
function BudgetDetailsPanel({ status }) {
  if (!status || status.status === 'none' || status.budgetTotal == null) return null;
  // Statut partiel issu de la liste (badge seul) : on attend le détail complet.
  if (status.consumedDays === undefined) return null;
  const cell = { display: 'flex', flexDirection: 'column', gap: 2 };
  const lbl = { fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 };
  const val = { fontSize: 14, fontWeight: 600, color: 'var(--text)' };
  return (
    <div
      style={{
        marginTop: 12,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10,
        padding: 12,
        background: 'var(--glass)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <div style={cell}>
        <span style={lbl}>Budget</span>
        <span style={val}>{fmtChf(status.budgetTotal)} CHF</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDays(status.budgetDays)} équiv.</span>
      </div>
      <div style={cell}>
        <span style={lbl}>Consommé</span>
        <span style={val}>{fmtDays(status.consumedDays)}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtChf(status.consumedChf)} CHF</span>
      </div>
      <div style={cell}>
        <span style={lbl}>Jours restants</span>
        <span style={{ ...val, color: status.budgetDays != null && (status.budgetDays - status.consumedDays) < 0 ? 'var(--danger)' : 'var(--text)' }}>
          {status.budgetDays != null ? fmtDays(status.budgetDays - status.consumedDays) : '—'}
        </span>
      </div>
      <div style={cell}>
        <span style={lbl}>TJM moyen</span>
        <span style={val}>{status.avgTjm != null ? `${fmtChf(status.avgTjm)} CHF/j` : '—'}</span>
      </div>
      <div style={cell}>
        <span style={lbl}>CJM moyen</span>
        <span style={val}>{status.avgCjm != null ? `${fmtChf(status.avgCjm)} CHF/j` : '—'}</span>
      </div>
      <div style={cell}>
        <span style={lbl}>Marge / jour</span>
        <span style={{ ...val, color: status.marginPerDay != null ? (status.marginPerDay >= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text)' }}>
          {status.marginPerDay != null ? `${fmtChf(status.marginPerDay)} CHF/j` : '—'}
        </span>
      </div>
      {status.switchDate ? (
        <div style={{ ...cell, gridColumn: '1 / -1' }}>
          <span style={lbl}>Bascule dans le rouge prévue le</span>
          <span style={{ ...val, color: 'var(--danger)' }}>
            {new Date(status.switchDate).toLocaleDateString('fr-CH')}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function BudgetBadge({ status }) {
  if (!status || status.status === 'none' || status.budgetTotal == null) return null;
  const color = BUDGET_STATUS_COLOR[status.status] ?? 'var(--text-muted)';
  const pct = status.consumptionPct != null ? Math.round(status.consumptionPct) : null;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        background: color,
        color: '#fff',
        whiteSpace: 'nowrap',
      }}
      title={`Consommé ${fmtChf(status.consumedChf)} / ${fmtChf(status.budgetTotal)} CHF`}
    >
      {fmtChf(status.consumedChf)} / {fmtChf(status.budgetTotal)} CHF{pct != null ? ` — ${pct}%` : ''}
    </span>
  );
}

function toDateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/** Le picker `<input type="color">` renvoie "#RRGGBB" — on stocke sans `#`. */
function colorWithHash(c) {
  if (!c) return '#326496';
  return c.startsWith('#') ? c : `#${c}`;
}

function colorStripHash(c) {
  if (!c) return '';
  return c.startsWith('#') ? c.slice(1) : c;
}

export default function AffairesView({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');
  const { conditions: filterConditions } = useFilterStore('affaires');
  const { sort, setSort } = useSortStore(AFFAIRES_SORT);
  // Multisélection : ids des affaires cochées (actions groupées).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // synotis-support#19 — modale des suggestions d'archivage (ADMIN).
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [teamCount, setTeamCount] = useState(undefined);
  // Ticket #76 — compteur de l'onglet Tâches (pattern Float « Team1 »).
  const [taskCount, setTaskCount] = useState(undefined);
  const [activeTab, setActiveTab] = useState('info');
  // Sprint 23 — statut budget par affaire (badge liste + alerte).
  const [budgetStatuses, setBudgetStatuses] = useState({});
  const [editBudget, setEditBudget] = useState(null);
  // Défauts globaux (placeholders des surcharges par affaire).
  const [budgetDefaults, setBudgetDefaults] = useState({
    thresholdPct: 90,
    remainingDays: 5,
  });

  useEffect(() => {
    apiSettings
      .get()
      .then((s) =>
        setBudgetDefaults({
          thresholdPct: s?.budgetAlertThresholdPct ?? 90,
          remainingDays: s?.budgetAlertRemainingDaysDefault ?? 5,
        }),
      )
      .catch(() => {});
  }, []);

  const sortedItems = useMemo(
    () =>
      sortRows(items, sort, {
        name: (a) => a.name,
        code: (a) => a.code,
        client: (a) => a.client?.name,
        projectManager: (a) =>
          a.projectManager ? `${a.projectManager.lastName} ${a.projectManager.firstName}` : null,
        status: (a) => {
          const idx = PROJECT_STATUSES.findIndex((s) => s.value === a.status);
          return idx < 0 ? null : idx;
        },
        budget: (a) => budgetStatuses[a.id]?.consumptionPct ?? null,
      }),
    [items, sort, budgetStatuses],
  );

  // Une ligne en consultation seule (#108) n'est pas sélectionnable.
  const isSelectable = (a) => affaireLevel(a, isAdmin) !== 'read';
  const selectableItems = sortedItems.filter(isSelectable);
  const selectedItems = items.filter((a) => selectedIds.has(a.id));
  const allSelected =
    selectableItems.length > 0 && selectableItems.every((a) => selectedIds.has(a.id));

  // Après rechargement (filtre, archivage…), on ne garde que les lignes encore affichées.
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(items.map((a) => a.id));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(selectableItems.map((a) => a.id)));

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiAffaires.list(
        archivedFilter,
        toFilterQuery(filterConditions),
      );
      setItems(data);
      // Sprint 23 — statut budget des affaires budgétées (badge de la liste),
      // en un seul appel. Best-effort, non bloquant.
      if (data.some((a) => Number(a.budgetTotal) > 0)) {
        apiBudget
          .statuses(archivedFilter)
          .then(({ items: statuses }) => {
            const map = {};
            for (const s of statuses) map[s.affaireId] = s;
            setBudgetStatuses(map);
          })
          .catch(() => setBudgetStatuses({}));
      } else {
        setBudgetStatuses({});
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter, filterConditions]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Clients et personnes (listes des sélecteurs de la fiche) : chargés une
  // seule fois par visite de l'écran, pas à chaque ouverture de fiche
  // (`/api/persons` coûte ~0,7 s). Un échec est rejoué à l'ouverture suivante.
  const refsPromiseRef = useRef(null);
  const loadRefs = () => {
    if (!refsPromiseRef.current) {
      refsPromiseRef.current = Promise.all([apiClients.list('active'), apiPersons.list('active')])
        .then(([c, p]) => {
          setClients(c.items);
          setPersons(p.items);
        })
        .catch((e) => {
          refsPromiseRef.current = null;
          throw e;
        });
    }
    return refsPromiseRef.current;
  };

  const openCreate = async () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setTeamCount(undefined);
    setEditBudget(null);
    setActiveTab('info');
    await loadRefs();
    setModalOpen(true);
  };

  const openEdit = async (affaire) => {
    setEditing(affaire);
    setActiveTab('info');
    setEditBudget(budgetStatuses[affaire.id] ?? null);
    // #108 — le budget détaillé (montants) est réservé au niveau délégué.
    if (affaireLevel(affaire, isAdmin) !== 'read') {
      apiBudget
        .affaire(affaire.id)
        .then((s) => setEditBudget(s))
        .catch(() => {});
    }
    // Précharge le count Team pour afficher "Team N" dès l'ouverture, sans
    // attendre que l'utilisateur clique sur le tab.
    apiAffaireTeam
      .list(affaire.id, 'active')
      .then((r) => setTeamCount(r.items.length))
      .catch(() => setTeamCount(undefined));
    setForm({
      name: affaire.name,
      code: affaire.code ?? '',
      clientId: affaire.clientId,
      color: affaire.color ?? '326496',
      notes: affaire.notes ?? '',
      budgetType: affaire.budgetType ?? 'NONE',
      budgetTotal: affaire.budgetTotal ?? '',
      budgetPerPhase: Boolean(affaire.budgetPerPhase),
      budgetPriority: Boolean(affaire.budgetPriority),
      // TJM projet (stocké horaire) affiché en journalier : ×8.
      defaultHourlyRate: hourlyToTjm(affaire.defaultHourlyRate ?? ''),
      nonBillable: Boolean(affaire.nonBillable),
      tentative: Boolean(affaire.tentative),
      status: affaire.status ?? 'TENTATIVE',
      lockedTaskList: Boolean(affaire.lockedTaskList),
      allPmsSchedule: Boolean(affaire.allPmsSchedule),
      budgetAlertEnabled: Boolean(affaire.budgetAlertEnabled),
      planningOnly: Boolean(affaire.planningOnly),
      isInternal: Boolean(affaire.isInternal),
      expiresAt: toDateInputValue(affaire.expiresAt),
      budgetAlertThresholdPct:
        affaire.budgetAlertThresholdPct != null ? String(affaire.budgetAlertThresholdPct) : '',
      budgetAlertRemainingDays:
        affaire.budgetAlertRemainingDays != null ? String(affaire.budgetAlertRemainingDays) : '',
      startDate: toDateInputValue(affaire.startDate),
      endDate: toDateInputValue(affaire.endDate),
      projectManagerId: affaire.projectManagerId ?? '',
      gescomProposalId: affaire.gescomProposalId ?? '',
      gescomOrderId: affaire.gescomOrderId ?? '',
      gescomProjectId: affaire.gescomProjectId ?? '',
    });
    setFormError(null);
    await loadRefs();
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const budgetStr = String(form.budgetTotal ?? '').trim();
      // TJM projet saisi (CHF/jour) → stockage horaire (÷8).
      const rateStr = String(tjmToHourly(form.defaultHourlyRate) ?? '').trim();
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        clientId: form.clientId,
        color: form.color || null,
        notes: form.notes.trim() || null,
        budgetType: form.budgetType,
        budgetTotal: budgetStr === '' ? null : budgetStr,
        budgetPerPhase: form.budgetPerPhase,
        budgetPriority: form.budgetPriority,
        defaultHourlyRate: rateStr === '' ? null : rateStr,
        nonBillable: form.nonBillable,
        tentative: form.tentative,
        status: form.status,
        lockedTaskList: form.lockedTaskList,
        allPmsSchedule: form.allPmsSchedule,
        budgetAlertEnabled: form.budgetAlertEnabled,
        planningOnly: form.planningOnly,
        isInternal: form.isInternal,
        expiresAt: form.expiresAt || null,
        budgetAlertThresholdPct:
          String(form.budgetAlertThresholdPct).trim() === ''
            ? null
            : Number(form.budgetAlertThresholdPct),
        budgetAlertRemainingDays:
          String(form.budgetAlertRemainingDays).trim() === ''
            ? null
            : Number(form.budgetAlertRemainingDays),
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        projectManagerId: form.projectManagerId || null,
        gescomProposalId: form.gescomProposalId.trim() || null,
        gescomOrderId: form.gescomOrderId.trim() || null,
        gescomProjectId: form.gescomProjectId.trim() || null,
      };
      if (editing) {
        // #108 — hors ADMIN, seuls les champs délégués partent (schéma strict).
        const body = isAdmin
          ? payload
          : Object.fromEntries(DELEGATED_FIELDS.map((k) => [k, payload[k]]));
        await apiAffaires.update(editing.id, body);
      } else {
        await apiAffaires.create(payload);
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (a) => {
    if (!window.confirm(`Archiver l'affaire "${a.name}" ?`)) return;
    await apiAffaires.archive(a.id);
    await reload();
  };

  const onRestore = async (a) => {
    await apiAffaires.restore(a.id);
    await reload();
  };

  // Suppression DÉFINITIVE (hard delete) — uniquement depuis la vue archivée.
  // Cascade : allocations, saisies, membres d'équipe, phases de l'affaire sont
  // supprimés avec elle. Double confirmation explicite.
  const onDelete = async (a) => {
    if (
      !window.confirm(
        `Supprimer DÉFINITIVEMENT l'affaire "${a.name}" ?\n\n` +
          'Cette action est IRRÉVERSIBLE et supprime aussi toutes les allocations, ' +
          'saisies de temps, membres d’équipe et phases rattachés.',
      )
    )
      return;
    try {
      await apiAffaires.destroy(a.id);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  // Actions groupées : chaque affaire passe par la route unitaire (mêmes droits
  // et mêmes gardes serveur) ; on poursuit sur les autres en cas d'échec.
  const bulkArchivable = selectedItems.filter((a) => !a.archivedAt);
  const bulkRestorable = selectedItems.filter((a) => a.archivedAt);
  // Suppression définitive : ADMIN, affaires déjà archivées (garde serveur).
  const bulkDeletable = isAdmin ? selectedItems.filter((a) => a.archivedAt) : [];

  const runBulk = async (targets, action) => {
    setBulkBusy(true);
    setError(null);
    const failures = [];
    for (const a of targets) {
      try {
        await action(a.id);
      } catch (err) {
        failures.push(`${a.name} : ${err.message}`);
      }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    if (failures.length) {
      setError(`${failures.length} échec(s) — ${failures.join(' · ')}`);
    }
    await reload();
  };

  const onBulkArchive = () => {
    const n = bulkArchivable.length;
    if (!window.confirm(`Archiver ${n} affaire${n > 1 ? 's' : ''} ?`)) return;
    runBulk(bulkArchivable, apiAffaires.archive);
  };

  const onBulkRestore = () => runBulk(bulkRestorable, apiAffaires.restore);

  const onBulkDelete = () => {
    const n = bulkDeletable.length;
    if (
      !window.confirm(
        `Supprimer DÉFINITIVEMENT ${n} affaire${n > 1 ? 's' : ''} archivée${n > 1 ? 's' : ''} ?\n\n` +
          'Cette action est IRRÉVERSIBLE et supprime aussi toutes les allocations, ' +
          'saisies de temps, membres d’équipe et phases rattachés.',
      )
    )
      return;
    runBulk(bulkDeletable, apiAffaires.destroy);
  };

  const bulkActions = [
    { key: 'archive', label: 'Archiver', icon: Archive, color: 'var(--warning)', count: bulkArchivable.length, onClick: onBulkArchive },
    { key: 'restore', label: 'Restaurer', icon: RotateCcw, color: 'var(--success)', count: bulkRestorable.length, onClick: onBulkRestore },
    ...(isAdmin
      ? [{ key: 'delete', label: 'Supprimer', icon: Trash2, color: 'var(--danger)', count: bulkDeletable.length, onClick: onBulkDelete, disabledHint: 'Suppression définitive : archivez d’abord les affaires' }]
      : []),
  ];

  // #108 — niveau sur l'affaire ouverte (création : ADMIN uniquement).
  const editLevel = editing ? affaireLevel(editing, isAdmin) : 'full';

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          // Ticket #34 — sur écran étroit, les filtres passent à la ligne au
          // lieu d'écraser le bouton d'action.
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Affaires</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <FilterBar scope="affaires" />
        </div>
        {isAdmin ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setSuggestionsOpen(true)}
              title="Affaires sans pointage récent ni planning à venir"
              style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 'var(--radius-pill)' }}
            >
              <Archive size={14} /> Suggestions d’archivage
            </button>
            <button type="button" onClick={openCreate} style={primaryBtn}>
              <Plus size={14} /> Nouvelle affaire
            </button>
          </div>
        ) : null}
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <BulkActionsBar
        count={selectedIds.size}
        actions={bulkActions}
        busy={bulkBusy}
        onClear={() => setSelectedIds(new Set())}
      />

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', width: 36 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={selectableItems.length === 0}
                  onChange={toggleAll}
                  title="Tout sélectionner"
                  aria-label="Tout sélectionner"
                />
              </th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Couleur</th>
              <SortableTh field="name" sort={sort} onSort={setSort}>Nom</SortableTh>
              <SortableTh field="code" sort={sort} onSort={setSort}>Code</SortableTh>
              <SortableTh field="client" sort={sort} onSort={setSort}>Client</SortableTh>
              <SortableTh field="projectManager" sort={sort} onSort={setSort}>Project Manager</SortableTh>
              <SortableTh field="status" sort={sort} onSort={setSort}>Statut</SortableTh>
              <SortableTh field="budget" sort={sort} onSort={setSort}>Budget</SortableTh>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucune affaire.</td>
              </tr>
            ) : (
              sortedItems.map((a) => (
                <tr
                  key={a.id}
                  style={{
                    borderTop: '1px solid var(--glass-border)',
                    background: selectedIds.has(a.id) ? 'var(--glass)' : undefined,
                  }}
                >
                  <td style={{ padding: '10px 12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(a.id)}
                      disabled={!isSelectable(a)}
                      onChange={() => toggleSelected(a.id)}
                      title={isSelectable(a) ? `Sélectionner ${a.name}` : 'Consultation seule'}
                      aria-label={`Sélectionner ${a.name}`}
                    />
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        background: a.color ? `#${a.color}` : 'transparent',
                        border: '1px solid var(--glass-border)',
                      }}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                    <button
                      type="button"
                      onClick={() => openEdit(a)}
                      title={affaireLevel(a, isAdmin) === 'read' ? "Consulter l'affaire" : "Modifier l'affaire"}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        margin: 0,
                        font: 'inherit',
                        color: 'var(--accent)',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      {a.name}
                    </button>
                    {a.isInternal ? (
                      <span style={{ marginLeft: 8 }}>
                        <InternalAffaireBadge expiresAt={a.expiresAt} />
                      </span>
                    ) : null}
                    {a.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivée)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{a.code ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{a.client?.name ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {a.projectManager
                      ? `${a.projectManager.firstName} ${a.projectManager.lastName}`
                      : '—'}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 500,
                        background: STATUS_BG[a.status] ?? 'var(--glass)',
                        color: '#fff',
                      }}
                    >
                      {PROJECT_STATUSES.find((s) => s.value === a.status)?.label ?? a.status ?? '—'}
                    </span>
                    {a.planningOnly ? (
                      <span style={{ marginLeft: 6 }}>
                        <PlanningOnlyBadge />
                      </span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {budgetStatuses[a.id] && budgetStatuses[a.id].status !== 'none' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <BudgetBadge status={budgetStatuses[a.id]} />
                        {a.budgetAlertEnabled &&
                        (budgetStatuses[a.id].status === 'warning' ||
                          budgetStatuses[a.id].status === 'over') ? (
                          <span
                            title="Affaire en alerte budget"
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: 999,
                              background: 'var(--danger)',
                            }}
                          />
                        ) : null}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {affaireLevel(a, isAdmin) === 'read' ? (
                      <button
                        type="button"
                        onClick={() => openEdit(a)}
                        title="Consulter"
                        aria-label="Consulter"
                        style={viewBtn}
                      >
                        <Eye size={14} />
                      </button>
                    ) : (
                      <EntityActions
                        archived={Boolean(a.archivedAt)}
                        onEdit={() => openEdit(a)}
                        onArchive={() => onArchive(a)}
                        onRestore={() => onRestore(a)}
                        // Suppression définitive : ADMIN uniquement.
                        onDelete={isAdmin ? () => onDelete(a) : undefined}
                      />
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          !editing
            ? 'Nouvelle affaire'
            : editLevel === 'read'
              ? editing.name
              : "Modifier l'affaire"
        }
        footer={
          editLevel !== 'read' && (activeTab === 'info' || activeTab === 'gescom' || !editing) ? (
            <>
              <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
                Annuler
              </button>
              <button
                type="submit"
                form={activeTab === 'gescom' ? 'affaire-gescom-form' : 'affaire-form'}
                disabled={submitting || !form.name.trim() || !form.clientId}
                style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
              >
                Enregistrer
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Fermer
            </button>
          )
        }
      >
        <TabbedEditor
          activeKey={activeTab}
          onChange={setActiveTab}
          tabs={[
            {
              key: 'info',
              label: 'Info',
              content: (
                <AffaireInfoForm
                  form={form}
                  setForm={setForm}
                  clients={clients}
                  persons={persons}
                  formError={formError}
                  onSubmit={onSubmit}
                  editing={editing}
                  budgetStatus={editBudget}
                  budgetDefaults={budgetDefaults}
                  level={editLevel}
                  canChangeProjectManager={
                    isAdmin || !editing || Boolean(editing.access?.canChangeProjectManager)
                  }
                />
              ),
            },
            // #108 — références Gescom : ADMIN uniquement (elles pilotent l'export).
            ...(isAdmin
              ? [{
                  key: 'gescom',
                  label: 'GESCOM',
                  content: <AffaireGescomForm form={form} setForm={setForm} onSubmit={onSubmit} affaireId={editing?.id} />,
                }]
              : []),
            // L'onglet Équipe n'est disponible qu'en édition (besoin de l'id de
            // l'affaire pour rattacher des membres).
            ...(editing
              ? [{
                  key: 'team',
                  label: 'Équipe',
                  count: teamCount,
                  content: (
                    <AffaireTeamTab
                      affaireId={editing.id}
                      canEdit={editLevel !== 'read'}
                      showRates={editLevel !== 'read'}
                      showCostRates={isAdmin}
                      onCountChange={setTeamCount}
                    />
                  ),
                }, {
                  // Ticket #76 — même contrainte que l'onglet Équipe : il faut
                  // l'id de l'affaire pour rattacher quoi que ce soit.
                  key: 'tasks',
                  label: 'Tâches',
                  count: taskCount,
                  content: (
                    <AffaireTasksTab
                      affaireId={editing.id}
                      canEdit={editLevel !== 'read'}
                      onCountChange={setTaskCount}
                    />
                  ),
                }]
              : []),
          ]}
        />
      </Modal>

      {isAdmin ? (
        <ArchiveSuggestionsModal
          open={suggestionsOpen}
          onClose={() => setSuggestionsOpen(false)}
          onArchived={reload}
        />
      ) : null}
    </div>
  );
}

/**
 * Sous-composant : onglet GESCOM — références de l'ERP commercial renseignées
 * lors de la création/mise à jour des affaires depuis Gescom (via l'API publique
 * M2M) et éditables ici. Propres au form principal (sauvegarde via Enregistrer).
 */
function AffaireGescomForm({ form, setForm, onSubmit, affaireId }) {
  const fields = [
    { key: 'gescomProposalId', label: 'N° proposition commerciale Gescom' },
    { key: 'gescomOrderId', label: 'N° commande Gescom' },
    { key: 'gescomProjectId', label: 'N° projet Gescom' },
  ];
  return (
    <form id="affaire-gescom-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
      <div>
        <div style={sectionTitle}>Références Gescom</div>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
          Identifiants issus de Gescom. Renseignés automatiquement lors de la
          synchronisation depuis Gescom ; modifiables manuellement.
        </p>
        <div style={{ display: 'grid', gap: 12 }}>
          {fields.map((f) => (
            <div key={f.key}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                {f.label}
              </label>
              {f.key === 'gescomProjectId' ? (
                // Le numéro de projet est celui que l'Export Gescom pousse :
                // résolu dans le catalogue Gescom, et choisissable sans le
                // connaître par cœur.
                <GescomProjectField
                  value={form.gescomProjectId}
                  onChange={(v) => setForm((cur) => ({ ...cur, gescomProjectId: v }))}
                  affaireId={affaireId}
                  affaireName={form.name}
                  inputStyle={inputStyle}
                />
              ) : (
                <input
                  type="text"
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  maxLength={128}
                  placeholder="—"
                  style={inputStyle}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}

/**
 * Sous-composant : formulaire Info (extrait pour pouvoir le rendre dans
 * un tab ou seul à la création).
 */
function AffaireInfoForm({
  form,
  setForm,
  clients,
  persons,
  formError,
  onSubmit,
  editing,
  budgetStatus,
  budgetDefaults = { thresholdPct: 90, remainingDays: 5 },
  level = 'full',
  canChangeProjectManager = true,
}) {
  // #108 — `read` : consultation (tout désactivé, budget masqué) ; `delegated` :
  // client, code, planification et caractère interne réservés à l'ADMIN.
  const readOnly = level === 'read';
  const restricted = level !== 'full';
  // Le client courant reste affichable même hors de la liste chargée.
  const clientOptions =
    editing?.client && !clients.some((c) => c.id === editing.client.id)
      ? [editing.client, ...clients]
      : clients;
  return (
        <form id="affaire-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 16 }}>
          {restricted ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {readOnly
                ? 'Consultation : vous intervenez sur cette affaire sans en être responsable.'
                : 'Vous êtes responsable de cette affaire : le client, le code, les références Gescom et le caractère interne restent réservés aux administrateurs.'}
            </div>
          ) : null}
          <fieldset
            disabled={readOnly}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: 16 }}
          >
          {/* Identité */}
          <div>
            <div style={sectionTitle}>Identité</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Nom
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  maxLength={255}
                  autoFocus
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Code (optionnel)
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  maxLength={64}
                  placeholder="PROJ-2026-001"
                  disabled={restricted}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Couleur
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="color"
                    value={colorWithHash(form.color)}
                    onChange={(e) => setForm({ ...form, color: colorStripHash(e.target.value) })}
                    style={{
                      width: 48,
                      height: 36,
                      padding: 0,
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-sm)',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  />
                  <input
                    type="text"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: colorStripHash(e.target.value.trim()) })}
                    placeholder="RRGGBB"
                    maxLength={6}
                    style={{ ...inputStyle, fontFamily: 'monospace' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Client + PM */}
          <div>
            <div style={sectionTitle}>Client et pilotage</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Client
                </label>
                <select
                  value={form.clientId}
                  onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                  required
                  disabled={restricted}
                  style={inputStyle}
                >
                  <option value="">— Sélectionner un client —</option>
                  {clientOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Project Manager
                </label>
                <select
                  value={form.projectManagerId}
                  onChange={(e) => setForm({ ...form, projectManagerId: e.target.value })}
                  disabled={!canChangeProjectManager}
                  title={
                    canChangeProjectManager
                      ? undefined
                      : 'Seul un responsable du client peut changer le project manager'
                  }
                  style={inputStyle}
                >
                  <option value="">— Aucun —</option>
                  {persons.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Période */}
          <div>
            <div style={sectionTitle}>Période</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Date de début
                </label>
                <DatePicker
                  value={form.startDate}
                  onChange={(v) => setForm({ ...form, startDate: v })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Date de fin
                </label>
                <DatePicker
                  value={form.endDate}
                  onChange={(v) => setForm({ ...form, endDate: v })}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Statut */}
          <div>
            <div style={sectionTitle}>Statut</div>
            <div style={{ display: 'grid', gap: 8 }}>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                style={inputStyle}
              >
                {PROJECT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {/* #108 — planification et caractère interne : ADMIN uniquement. */}
              {!restricted ? (
              <>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, planningOnly: !form.planningOnly })}
                  title={PLANNING_ONLY_TITLE}
                  aria-pressed={form.planningOnly}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: 13,
                    cursor: 'pointer',
                    background: form.planningOnly ? 'var(--warning)' : 'var(--glass)',
                    color: form.planningOnly ? '#fff' : 'var(--text)',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  Planification uniquement
                </button>
                {form.planningOnly ? (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Imputations de temps fermées (planning autorisé).
                  </span>
                ) : null}
              </div>
              {/* Ticket #54 — affaire interne : hors export Gescom, saisies non facturables. */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, isInternal: !form.isInternal })}
                  title={INTERNAL_AFFAIRE_TITLE}
                  aria-pressed={form.isInternal}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 14px',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: 13,
                    cursor: 'pointer',
                    background: form.isInternal ? 'var(--accent)' : 'var(--glass)',
                    color: form.isInternal ? 'var(--card-bg)' : 'var(--text)',
                    border: '1px solid var(--glass-border)',
                  }}
                >
                  Affaire interne
                </button>
                {form.isInternal ? (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                    Expire le
                    <DatePicker
                      value={form.expiresAt}
                      onChange={(v) => setForm({ ...form, expiresAt: v })}
                      ariaLabel="Date d'expiration"
                      style={{ ...inputStyle, width: 'auto' }}
                    />
                  </label>
                ) : null}
              </div>
              {form.isInternal ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Jamais exportée vers Gescom ; les saisies y sont enregistrées non facturables.
                </span>
              ) : null}
              </>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <label style={checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.tentative}
                    onChange={(e) => setForm({ ...form, tentative: e.target.checked })}
                  />
                  Tentative (avant-vente)
                </label>
                <label style={checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.nonBillable}
                    onChange={(e) => setForm({ ...form, nonBillable: e.target.checked })}
                  />
                  Non facturable
                </label>
                <label style={checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.lockedTaskList}
                    onChange={(e) => setForm({ ...form, lockedTaskList: e.target.checked })}
                  />
                  Liste de tâches verrouillée
                </label>
                <label style={checkboxRow}>
                  <input
                    type="checkbox"
                    checked={form.allPmsSchedule}
                    onChange={(e) => setForm({ ...form, allPmsSchedule: e.target.checked })}
                  />
                  Tous les PM peuvent planifier
                </label>
              </div>
            </div>
          </div>

          {/* Budget — masqué au niveau `read` (#108 : ni montants ni TJM). */}
          {readOnly ? null : (
          <div>
            <div style={sectionTitle}>Budget</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Type de budget
                </label>
                <select
                  value={form.budgetType}
                  onChange={(e) => setForm({ ...form, budgetType: e.target.value })}
                  style={inputStyle}
                >
                  {BUDGET_TYPES.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  Budget total
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.budgetTotal}
                  onChange={(e) => setForm({ ...form, budgetTotal: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                  TJM par défaut du projet (CHF/jour)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.defaultHourlyRate}
                  onChange={(e) => setForm({ ...form, defaultHourlyRate: e.target.value })}
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
              <label style={checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.budgetPerPhase}
                  onChange={(e) => setForm({ ...form, budgetPerPhase: e.target.checked })}
                />
                Budget par phase
              </label>
              <label style={checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.budgetPriority}
                  onChange={(e) => setForm({ ...form, budgetPriority: e.target.checked })}
                />
                Budget priorité
              </label>
            </div>
            {/* Sprint 23 — alerte budget + badge de consommation */}
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setForm({ ...form, budgetAlertEnabled: !form.budgetAlertEnabled })}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 13,
                  cursor: 'pointer',
                  background: form.budgetAlertEnabled ? 'var(--accent)' : 'var(--glass)',
                  color: form.budgetAlertEnabled ? '#fff' : 'var(--text)',
                  border: '1px solid var(--glass-border)',
                }}
                aria-pressed={form.budgetAlertEnabled}
              >
                Alerte budget (90%)
              </button>
              {editing ? <BudgetBadge status={budgetStatus} /> : null}
            </div>
            {/* Surcharge des seuils d'alerte par affaire (vide = hérite du défaut global) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                Seuil alerte % (défaut global : {budgetDefaults.thresholdPct})
                <input
                  type="number"
                  min={0}
                  max={100}
                  placeholder={String(budgetDefaults.thresholdPct)}
                  value={form.budgetAlertThresholdPct}
                  onChange={(e) => setForm({ ...form, budgetAlertThresholdPct: e.target.value })}
                  style={{ width: 160, padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)', background: 'var(--card-bg)', color: 'var(--text)' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                Marge d'alerte (jours restants, défaut : {budgetDefaults.remainingDays})
                <input
                  type="number"
                  min={0}
                  max={3650}
                  placeholder={String(budgetDefaults.remainingDays)}
                  value={form.budgetAlertRemainingDays}
                  onChange={(e) => setForm({ ...form, budgetAlertRemainingDays: e.target.value })}
                  style={{ width: 200, padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)', background: 'var(--card-bg)', color: 'var(--text)' }}
                />
              </label>
            </div>
            {editing ? <BudgetDetailsPanel status={budgetStatus} /> : null}
          </div>

          )}

          {/* Notes */}
          <div>
            <div style={sectionTitle}>Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              maxLength={10000}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          {/* Tags (lecture seule) */}
          <div>
            <div style={sectionTitle}>Tags (importés depuis Float)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {editing?.tags && Array.isArray(editing.tags) && editing.tags.length > 0 ? (
                editing.tags.map((t, idx) => (
                  <div
                    key={`${String(t)}-${idx}`}
                    style={{
                      background: 'var(--glass)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 999,
                      padding: '4px 10px',
                      fontSize: 12,
                      color: 'var(--text)',
                    }}
                  >
                    {typeof t === 'string' ? t : t?.name ?? '—'}
                  </div>
                ))
              ) : (
                <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
              )}
            </div>
          </div>

          </fieldset>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
  );
}
